import { useRequestFeedback } from '@reflag/react-sdk';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { useEventCallback } from '@gitbook/util-react-hooks';
import type { DistributiveOmit } from '@gitbook/util-ts';

import type { AgentChatMessageSource } from '../AgentChat';
import type { GitBookAgentClientState } from './GitBookAgentClient';

/**
 * What a conversation's events are attributed to.
 *
 * A conversation held against a change request reports the space and the change request, as the
 * change-request agent always has. One held against an organization has neither, so it reports the
 * organization alone — every context field of these events is optional for that reason.
 *
 * The kinds are named after their agents, so the scope is also what fills in the `agentType` the
 * events report: their names carry no agent of their own.
 */
export type AgentChatAnalyticsScope =
    | {
          kind: 'change_request';
          space: api.Space;
          changeRequestId: string;
      }
    | {
          kind: 'organization';
          organizationId: string;
          /** Where the user is, so the events tell a question about analytics from one about docs. */
          place?: string;
      };

/**
 * Where the user opened the upgrade CTA from.
 */
export type AgentUpgradeOpenReason = 'in_flow_card' | 'above_input_banner' | 'agent_menu';

/**
 * Report what happens in an agent conversation.
 *
 * Returns the tracking callbacks {@link AgentChatProps} takes, so any surface hosting the chat
 * reports the same events, plus the two the usage limit needs.
 */
export function useAgentChatAnalytics(scope: AgentChatAnalyticsScope) {
    const requestFeedback = useRequestFeedback();

    const logAgentEvent = useEventCallback(
        (event: DistributiveOmit<analytics.AgentChatAnalyticsEvents, 'agentType'>) => {
            const agentEvent = {
                ...(scope.kind === 'organization' && scope.place ? { place: scope.place } : {}),
                ...event,
                agentType: scope.kind,
            };
            analytics.logEvent(
                scope.kind === 'change_request'
                    ? analytics.createChangeRequestEvent(
                          scope.space,
                          scope.changeRequestId,
                          agentEvent
                      )
                    : analytics.createOrgEvent(scope.organizationId, agentEvent)
            );
        }
    );

    const onTrackPostMessage = useEventCallback(
        (input: {
            messageSource: AgentChatMessageSource;
            message: string;
            previousMessagesCount: number;
            messagesSentLast30Days: number;
            attachmentsCount: number;
            imageAttachmentsCount: number;
            fileAttachmentsCount: number;
            importFileAttachmentsCount: number;
        }) => {
            logAgentEvent({ type: 'agent_message_post', ...input });
        }
    );

    const onTrackStopResponse = useEventCallback((state: GitBookAgentClientState) => {
        logAgentEvent({
            type: 'agent_stop',
            messagesCount: state.messages.length,
            messageSteps: state.messages[state.messages.length - 1]?.steps?.length ?? 0,
        });
    });

    const onTrackPickTemplatePrompt = useEventCallback((input: { id: string }) => {
        logAgentEvent({ type: 'agent_pick_template_prompt', prompt: input.id });
    });

    const onTrackPickStarter = useEventCallback(
        (input: { starter: string; place: string; immediate: boolean }) => {
            logAgentEvent({ type: 'agent_pick_starter', ...input });
        }
    );

    const onTrackMessageFeedback = useEventCallback(
        (input: { responseId: string; sentiment: 'good' | 'bad' }) => {
            logAgentEvent({ type: 'agent_message_feedback', ...input });

            // A thumbs-down is the moment to ask what went wrong, while the answer is on screen.
            if (input.sentiment === 'bad') {
                requestFeedback({
                    flagKey: 'DOCS_AGENTS',
                    title: 'Help us improve GitBook Agent by telling us more about what went wrong',
                    openWithCommentVisible: true,
                    position: { type: 'MODAL' },
                });
            }
        }
    );

    const onTrackReachLimit = useEventCallback(
        (input: {
            messageSource: AgentChatMessageSource;
            messagesSentLast7Days: number;
            limit: number;
        }) => {
            logAgentEvent({ type: 'agent_message_reach_limit', ...input });
        }
    );

    const onTrackOpenUpgrade = useEventCallback((reason: AgentUpgradeOpenReason) => {
        logAgentEvent({ type: 'agent_message_open_upgrade', reason });
    });

    return React.useMemo(
        () => ({
            onTrackPostMessage,
            onTrackStopResponse,
            onTrackPickTemplatePrompt,
            onTrackPickStarter,
            onTrackMessageFeedback,
            onTrackReachLimit,
            onTrackOpenUpgrade,
        }),
        [
            onTrackPostMessage,
            onTrackStopResponse,
            onTrackPickTemplatePrompt,
            onTrackPickStarter,
            onTrackMessageFeedback,
            onTrackReachLimit,
            onTrackOpenUpgrade,
        ]
    );
}
